import { useCallback, useContext, useRef } from "react";
import { AgGridReact } from "ag-grid-react";
import {
  FeatureLogNavContext,
  FeatureLogNavFeature
} from "context/FeatureLogNavProvider";

type Props = {
  gridRef: React.RefObject<AgGridReact<any>>;
  feature: FeatureLogNavFeature;
  projectId: string;
};

/**
 * List-page side of the prev/next navigator. Reads the post-filter, post-sort
 * row order from the AG-Grid and writes it to FeatureLogNavContext so that
 * the details page can step through records in the same order.
 */
export const useFeatureLogNavWriter = ({
  gridRef,
  feature,
  projectId
}: Props) => {
  const { setSnapshot } = useContext(FeatureLogNavContext);
  // Tracks the last captured signature so we don't re-write identical snapshots
  // on every grid model update.
  const lastSignatureRef = useRef<string>("");

  const captureSnapshot = useCallback(() => {
    const api = gridRef.current?.api;
    if (!api || !projectId) return;

    const orderedIds: string[] = [];
    try {
      api.forEachNodeAfterFilterAndSort((node: any) => {
        if (node?.group) return;
        const id = node?.data?.id;
        if (id) orderedIds.push(id);
      });
    } catch {
      return;
    }

    let filterModel: any = null;
    try {
      filterModel = api.getFilterModel?.() ?? null;
    } catch {
      filterModel = null;
    }

    let sortModel: Array<{
      colId: string;
      sort: "asc" | "desc";
      sortIndex?: number | null;
    }> = [];
    try {
      const columnState = api.getColumnState?.() ?? [];
      sortModel = columnState
        .filter((c: any) => !!c.sort)
        .map((c: any) => ({
          colId: c.colId,
          sort: c.sort,
          sortIndex: c.sortIndex ?? null
        }));
    } catch {
      sortModel = [];
    }

    const signature = `${orderedIds.length}|${orderedIds.join(",")}|${JSON.stringify(
      filterModel
    )}|${JSON.stringify(sortModel)}`;
    if (signature === lastSignatureRef.current) return;
    lastSignatureRef.current = signature;

    setSnapshot({
      feature,
      projectId,
      orderedIds,
      filterModel,
      sortModel,
      updatedAt: Date.now()
    });
  }, [gridRef, feature, projectId, setSnapshot]);

  const onFirstDataRendered = useCallback(() => {
    captureSnapshot();
  }, [captureSnapshot]);

  const onSortChanged = useCallback(() => {
    captureSnapshot();
  }, [captureSnapshot]);

  const onFilterChanged = useCallback(() => {
    captureSnapshot();
  }, [captureSnapshot]);

  const onModelUpdated = useCallback(() => {
    captureSnapshot();
  }, [captureSnapshot]);

  return {
    captureSnapshot,
    onFirstDataRendered,
    onSortChanged,
    onFilterChanged,
    onModelUpdated
  };
};

export default useFeatureLogNavWriter;
